import { Component, OnDestroy, OnInit } from "@angular/core";
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterEvent,
  Event,
} from "@angular/router";
import { filter, startWith, Subscription } from "rxjs";
import { BrandCampaign } from "../../../shared/models/brandCampaign";
import { BrandCampaignDetails } from "../../../shared/models/brandCampaignDetail";
import { BrandService } from "../../../shared/services/api/brand.service";
import { AuthenticationService } from "../../../shared/services/api/authentication.service";
import { BrandCampaignPromoAdsPage } from "../brand-campaign-promo-ads/brand-campaign-promo-ads-page";
import { BrandUrl } from "../../brand.url";
import { PartnersSheetProcessingProgressService } from "../shared/services/partners-sheet-processing-progress.service";
import { BrandCampaignService } from "../shared/services/brand-campaign.service";
import { FeatureFlagsService } from "../../../shared/services/feature-flags.service";

enum NavigationItems {
  Dashboard = "dashboard",
  Partners = "partners",
  PostsCalendar = "posts-calendar",
  PostedScheduled = "posted-scheduled",
  Map = "map",
  InformationSettings = "information-settings",
  InformationDetails = "information-details",
  InformationInvitePage = "information-invite-page",
  Content = "content",
  Publications = "publications",
  PromotedAds = "promoted-ads",
  LandingPage = "landing-page",
  ContentCalendar = "content-calendar",
  InvitePartners = "invite-partners",
  Pos = "pos",
  Summary = "summary",
}

@Component({
  selector: "app-brand-campaign-navigation",
  templateUrl: "./brand-campaign-navigation.component.html",
  styleUrls: ["./brand-campaign-navigation.component.scss"],
  providers: [PartnersSheetProcessingProgressService],
})
export class BrandCampaignNavigationComponent implements OnInit, OnDestroy {
  protected readonly BrandUrl = BrandUrl;
  protected readonly NavigationItems = NavigationItems;
  public readonly BrandCampaignPromoAdsPage = BrandCampaignPromoAdsPage;
  public readonly isProspectPartnersEnabled: boolean;
  public campaign!: BrandCampaign;
  public campaignDetails!: BrandCampaignDetails;
  public promoAdsPage: BrandCampaignPromoAdsPage =
    BrandCampaignPromoAdsPage.Settings;
  public showPOS = false;
  public showLandingPage = false;
  public isViewingPromotedAdsPage = false;
  public isViewingInfoPage = false;
  public isContentPage = false;
  public partnersSheetProcessingProgress?: number;
  private readonly subscriptions = new Subscription();
  public isPartnersSectionSelected = false;
  protected isHeaderHidden = false;
  protected readonly isAdmin: boolean;
  protected readonly brandId: number;
  private readonly BetaCampaignSections = [
    NavigationItems.Partners,
    NavigationItems.InformationSettings,
    NavigationItems.InformationDetails,
    NavigationItems.InformationInvitePage,
    NavigationItems.Publications,
    NavigationItems.LandingPage,
    NavigationItems.InvitePartners,
    NavigationItems.Summary,
  ];

  constructor(
    authService: AuthenticationService,
    private readonly route: ActivatedRoute,
    private readonly brandCampaignService: BrandCampaignService,
    private readonly brandService: BrandService,
    private readonly featureFlagsService: FeatureFlagsService,
    private readonly partnersSheetProcessingProgressService: PartnersSheetProcessingProgressService,
    private readonly router: Router,
  ) {
    this.isAdmin = authService.isRoleAdmin();
    this.brandId = brandService.currentBrandId;
    this.isProspectPartnersEnabled =
      this.featureFlagsService.isProspectPartnersEnabled();

    if (this.isProspectPartnersEnabled) {
      this.subscriptions.add(
        this.partnersSheetProcessingProgressService.progress$.subscribe(
          (progress) => {
            this.partnersSheetProcessingProgress = progress;
          },
        ),
      );

      this.partnersSheetProcessingProgressService.check();
    }

    this.subscriptions.add(
      this.router.events
        .pipe(
          filter(
            (e: Event | RouterEvent): e is RouterEvent =>
              e instanceof NavigationEnd,
          ),
          startWith(this.router),
        )
        .subscribe((event: Router | RouterEvent) => {
          const url = event.url;
          this.isViewingPromotedAdsPage = url.indexOf("promoted-ads") !== -1;
          this.isViewingInfoPage = url.indexOf("information") !== -1;
          this.isContentPage = url.indexOf("publications") !== -1;
        }),
    );
  }

  public ngOnInit(): void {
    this.loadCampaign(+this.route.snapshot.params.campaignId);

    this.subscriptions.add(
      this.brandCampaignService.promoAdsTabChanged$.subscribe(
        (promoAdsPage) => (this.promoAdsPage = promoAdsPage),
      ),
    );

    this.subscriptions.add(
      this.route.queryParams.subscribe((params) => {
        this.promoAdsPage = params.page ?? BrandCampaignPromoAdsPage.Settings;
      }),
    );
  }

  private loadCampaign(campaignId: number): void {
    this.brandCampaignService.loadBrandCampaign(campaignId).then((campaign) => {
      if (campaign) {
        this.campaign = campaign;
        this.showPOS = !campaign.isDownloadOnly;
        this.showLandingPage = !campaign.isDownloadOnly;
      }
    });

    this.subscriptions.add(
      this.brandCampaignService.currentBrandCampaign$.subscribe((campaign) => {
        if (campaign) {
          this.campaign = campaign;
        }
      }),
    );
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();

    if (this.isProspectPartnersEnabled) {
      this.partnersSheetProcessingProgressService.clearInterval();
    }
  }

  public goToPromoAds(page: BrandCampaignPromoAdsPage): void {
    if (!this.campaign) {
      return;
    }
    this.promoAdsPage = page;
    const url =
      page === BrandCampaignPromoAdsPage.Settings
        ? BrandUrl.Campaign.AdsSettings
        : BrandUrl.Campaign.AdsChannels;

    this.router.navigateByUrl(
      url(this.brandService.currentBrandId, this.campaign.id),
    );
  }

  public partnersTabChanged(isActive: boolean): void {
    this.isPartnersSectionSelected = isActive;
  }

  protected isShown(section: NavigationItems): boolean {
    if (!this.campaign) {
      return false;
    }

    if (this.campaign?.isBeta) {
      return this.BetaCampaignSections.includes(section);
    }

    return section !== NavigationItems.Publications;
  }
}
